import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  IItemProcess,
  ItemProcessCategory,
} from '../../../../../../@store/products/item-processes/item-processes.service';
import {
  BehaviorSubject,
  combineLatest,
  map,
  Observable,
  startWith,
  Subject,
  takeUntil,
} from 'rxjs';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { ItemProcessesActions } from '../../../../../../@store/products/item-processes/item-processes.actions';
import { ManageItemsProcessesComponent } from '@components/products/item-processes/manage-items-processes/manage-items-processes.component';
import { CommonModule } from '@angular/common';
import { MatFormField, MatInput } from '@angular/material/input';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatButton } from '@angular/material/button';
import {
  selectAllItemProcesses,
  selectItemProcessCategories,
} from '../../../../../../@store/products/item-processes/item-processes.selectors';
import { MatLabel } from '@angular/material/form-field';
import { isLoadingState } from '../../../../../../@core/fetch-state.utils';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
} from '@angular/material/autocomplete';

export interface IManageProcessItems {
  category: string;
  id: number;
  description: string;
  specificGravity: string;
  lbPer1000ft: number | null;
  pricePerLb: number | null;
  pricePerUnit: number | null;
  itemId?: string;
  constant1?: number | null;
  constant2?: number | null;
}

@Component({
  selector: 'ciwc-ca-add-item-processes',
  imports: [
    FormsModule,
    CommonModule,
    MatDialogTitle,
    MatDialogContent,
    MatFormField,
    MatSelect,
    MatInput,
    MatFormField,
    MatDialogActions,
    MatDialogClose,
    MatButton,
    ReactiveFormsModule,
    MatOption,
    MatLabel,
    MatProgressSpinnerModule,
    MatAutocompleteModule,
  ],
  standalone: true,
  templateUrl: './ca-add-item-processes.component.html',
  styleUrl: './ca-add-item-processes.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CaAddItemProcessesComponent implements OnInit, OnDestroy {
  form: FormGroup;
  categories: ItemProcessCategory[] | undefined;
  allItemProcesses: IItemProcess[] = [];
  filteredItemProcesses$: Observable<IItemProcess[]>;
  private destroy$ = new Subject<void>();
  private readonly store = inject(Store);
  isLoadingCategories = true;
  isLoadingProcesses = true;
  private isInputFocused = false;

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<ManageItemsProcessesComponent>,
    private actions$: Actions,
    @Inject(MAT_DIALOG_DATA)
    public data: { categories: ItemProcessCategory[]; editData?: IManageProcessItems },
    private cdr: ChangeDetectorRef,
  ) {
    this.form = this.fb.group({
      category: ['', Validators.required],
      itemId: [''],
      description: [''],
      specificGravity: [null],
      lbPer1000ft: [null],
      pricePerLb: [null],
      pricePerUnit: [null],
      constant1: [null],
      constant2: [null],
    });

    // Setup autocomplete filtering with both category and search input
    this.filteredItemProcesses$ = combineLatest([
      this.form.get('category')?.valueChanges.pipe(startWith('')) ||
        new BehaviorSubject<string>(''),
      this.form.get('itemId')?.valueChanges.pipe(startWith('')) || new BehaviorSubject<string>(''),
    ]).pipe(
      map(([category, searchValue]) => {
        // If input is focused and no search value, show all items for the category
        if (this.isInputFocused && !searchValue) {
          return this._filterByCategory(category || '');
        }
        return this._filter(searchValue || '', category || '');
      })
    );

    this.form
      .get('category')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((category) => {
        // Store the current category value
        const currentCategory = this.form.get('category')?.value;

        // Reset all form controls except category
        this.form.patchValue({
          itemId: '',
          description: '',
          specificGravity: null,
          lbPer1000ft: null,
          pricePerLb: null,
          pricePerUnit: null,
          constant1: null,
          constant2: null
        }, { emitEvent: false });

        // Reset the read-only base price by triggering change detection
        this.cdr.detectChanges();

        // Update validators based on the new category
        this.updateValidators(category);
        this.cdr.markForCheck();
      });
  }

  ngOnInit(): void {
    this.getData();

    this.actions$
      .pipe(ofType(ItemProcessesActions.addItemProcessSuccess), takeUntil(this.destroy$))
      .subscribe(() => {
        this.dialogRef.close({ success: true, category: this.form.value.category });
      });

    this.actions$
      .pipe(ofType(ItemProcessesActions.updateItemProcessSuccess), takeUntil(this.destroy$))
      .subscribe(() => {
        this.dialogRef.close({ success: true, category: this.form.value.category });
      });

    this.actions$
      .pipe(ofType(ItemProcessesActions.addItemProcessFailure), takeUntil(this.destroy$))
      .subscribe(({ error }) => {
        console.error('Failed to add item process:', error);
      });
  }

  private getData(): void {
    this.isLoadingCategories = true;
    this.isLoadingProcesses = true;

    this.store
      .select(selectItemProcessCategories)
      .pipe(takeUntil(this.destroy$))
      .subscribe((categories: ItemProcessCategory[] | undefined) => {
        if (!categories || categories.length === 0) {
          this.store.dispatch(ItemProcessesActions.loadOnlyItemProcessCategories());
        }
        this.categories = categories;
        this.cdr.markForCheck();
      });

    this.store
      .select(selectAllItemProcesses)
      .pipe(takeUntil(this.destroy$))
      .subscribe((processes: IItemProcess[]) => {
        this.allItemProcesses = processes;
        this.cdr.markForCheck();
      });

    // Listen for loading state for categories
    this.store
      .select((state) => state.app.categories)
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        this.isLoadingCategories = isLoadingState(state);
        this.cdr.markForCheck();
      });

    // Listen for loading state for item processes
    this.store
      .select((state) => state.app.groups)
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        this.isLoadingProcesses = isLoadingState(state);
        this.cdr.markForCheck();
      });

    this.store.dispatch(
      ItemProcessesActions.loadItemProcesses({
        payload: {
          q: '',
          limit: -1,
        },
      }),
    );
  }

  private updateValidators(category: string): void {
    const lowerCaseCategory = category?.toLowerCase();

    // Clear all conditional validators
    this.form.get('specificGravity')?.clearValidators();
    this.form.get('lbPer1000ft')?.clearValidators();
    this.form.get('pricePerLb')?.clearValidators();
    this.form.get('pricePerUnit')?.clearValidators();
    this.form.get('constant1')?.clearValidators();
    this.form.get('constant2')?.clearValidators();

    // If "All" is selected, don't set any validators
    if (!lowerCaseCategory) {
      this.form.get('specificGravity')?.updateValueAndValidity();
      this.form.get('lbPer1000ft')?.updateValueAndValidity();
      this.form.get('pricePerLb')?.updateValueAndValidity();
      this.form.get('pricePerUnit')?.updateValueAndValidity();
      this.form.get('constant1')?.updateValueAndValidity();
      this.form.get('constant2')?.updateValueAndValidity();
      return;
    }

    // Set validators based on category
    switch (lowerCaseCategory) {
      case 'other':
        this.form.get('pricePerUnit')?.setValidators(Validators.required);
        break;
      case 'copper':
        this.form.get('lbPer1000ft')?.setValidators(Validators.required);
        this.form.get('pricePerLb')?.setValidators(Validators.required);
        break;
      case 'single_end':
        this.form.get('specificGravity')?.setValidators(Validators.required);
        this.form.get('constant1')?.setValidators(Validators.required);
        this.form.get('pricePerLb')?.setValidators(Validators.required);
        break;
      case 'compounds':
      case 'teflon_tapes':
        this.form.get('specificGravity')?.setValidators(Validators.required);
        this.form.get('pricePerLb')?.setValidators(Validators.required);
        break;
      case 'all_tapes_and_fillers':
        this.form.get('specificGravity')?.setValidators(Validators.required);
        this.form.get('constant1')?.setValidators(Validators.required);
        this.form.get('constant2')?.setValidators(Validators.required);
        this.form.get('pricePerLb')?.setValidators(Validators.required);
        break;
      default:
        if (lowerCaseCategory && lowerCaseCategory !== 'other') {
          this.form.get('specificGravity')?.setValidators(Validators.required);
        }
        break;
    }

    // Update validity for all potentially affected controls
    this.form.get('specificGravity')?.updateValueAndValidity();
    this.form.get('lbPer1000ft')?.updateValueAndValidity();
    this.form.get('pricePerLb')?.updateValueAndValidity();
    this.form.get('pricePerUnit')?.updateValueAndValidity();
    this.form.get('constant1')?.updateValueAndValidity();
    this.form.get('constant2')?.updateValueAndValidity();
  }

  displayFn = (itemId: string): string => {
    if (!itemId) return '';
    const process = this.allItemProcesses.find((p) => p.itemId === itemId);
    return process ? `${process.itemId} - ${process.description}` : itemId;
  };

  onInputFocus(): void {
    this.isInputFocused = true;
    // Trigger a new emission to show all items
    this.form.get('itemId')?.updateValueAndValidity();
  }

  private _filterByCategory(category: string): IItemProcess[] {
    const categoryValue = category.toLowerCase();
    // If category is empty (All selected), return all items
    if (!categoryValue) {
      return this.allItemProcesses;
    }
    return this.allItemProcesses.filter(process =>
      process.itemType.toLowerCase() === categoryValue
    );
  }

  private _filter(searchValue: string, category: string): IItemProcess[] {
    const filterValue = searchValue.toLowerCase();
    const categoryValue = category.toLowerCase();

    return this.allItemProcesses.filter((process) => {
      const matchesSearch =
        process.itemId.toLowerCase().includes(filterValue) ||
        process.description.toLowerCase().includes(filterValue);

      // If category is empty (All selected), only check search
      const matchesCategory = !categoryValue || process.itemType.toLowerCase() === categoryValue;

      return matchesSearch && matchesCategory;
    });
  }

  onItemIdSelectionChange(event: MatAutocompleteSelectedEvent): void {
    const value = event.option.value;
    if (value) {
      this.populateFormFromSelectedItem(value);
    }
  }

  private populateFormFromSelectedItem(itemId: string): void {
    const selectedProcess = this.allItemProcesses.find((process) => process.itemId === itemId);
    if (selectedProcess) {
      this.form.patchValue(
        {
          category: selectedProcess.itemType,
          description: selectedProcess.description,
          specificGravity: selectedProcess.specificGravity,
          lbPer1000ft: selectedProcess.lbPer1000ft,
          pricePerLb: selectedProcess.pricePerLb,
          pricePerUnit: selectedProcess.pricePerUnit,
          constant1: selectedProcess.constant1,
          constant2: selectedProcess.constant2,
        },
        { emitEvent: false },
      );

      // Update validators based on the new category
      this.updateValidators(selectedProcess.itemType);
      this.cdr.markForCheck();
    }
  }

  onSubmit(): void {
    if (this.form.valid) {
      const formValue = this.form.value;
      const selectedProcess = this.allItemProcesses.find(process => process.itemId === formValue.itemId);
      
      this.dialogRef.close({
        success: true,
        category: selectedProcess?.itemType || formValue.category,
        data: {
          id: Date.now(), // Generate a temporary ID
          itemType: selectedProcess?.itemType || formValue.category,
          itemId: formValue.itemId,
          description: formValue.description,
          specificGravity: formValue.specificGravity,
          lbPer1000ft: formValue.lbPer1000ft,
          pricePerLb: formValue.pricePerLb,
          pricePerUnit: formValue.pricePerUnit,
          constant1: formValue.constant1,
          constant2: formValue.constant2,
          innerDiameter: null,
          units: 1
        }
      });
    }
  }

  get showUniqueId(): boolean {
    // Always shown for all categories
    return true;
  }

  get showDescription(): boolean {
    // Always shown for all categories
    return true;
  }

  get showSpecificGravity(): boolean {
    const category = this.form.get('category')?.value?.toLowerCase();
    return (
      !category ||
      ['single_end', 'compounds', 'teflon_tapes', 'all_tapes_and_fillers'].includes(category)
    );
  }

  get showLbPer1000ft(): boolean {
    const category = this.form.get('category')?.value?.toLowerCase();
    return category === 'copper';
  }

  get showBasePriceUnit(): boolean {
    const category = this.form.get('category')?.value?.toLowerCase();
    return category === 'other';
  }

  get showBasePriceLb(): boolean {
    const category = this.form.get('category')?.value?.toLowerCase();
    return !category || ['compounds', 'teflon_tapes', 'all_tapes_and_fillers'].includes(category);
  }

  get showReadOnlyBasePrice(): boolean {
    const category = this.form.get('category')?.value?.toLowerCase();
    // Show for Single End and Copper. Note: Value is hardcoded to 6 in template.
    return ['single_end', 'copper'].includes(category);
  }

  get readOnlyBasePriceValue(): number | string {
    const selectedCategoryKey = this.form.get('category')?.value;
    const selectedItemId = this.form.get('itemId')?.value;
    if (!selectedCategoryKey || !this.categories || !selectedItemId) {
      return '';
    }
    const selectedCategory = this.categories.find((cat) => cat.key === selectedCategoryKey);
    return selectedCategory?.basePrice ?? '';
  }

  get showSurcharge(): boolean {
    const category = this.form.get('category')?.value?.toLowerCase();
    return ['copper', 'single_end'].includes(category);
  }

  get surchargeLabel(): string {
    const category = this.form.get('category')?.value?.toLowerCase();
    switch (category) {
      case 'copper':
        return 'Surcharge';
      case 'single_end':
        return 'Surcharge';
      default:
        return 'Base Price/LB*';
    }
  }

  get showConstant1(): boolean {
    const category = this.form.get('category')?.value?.toLowerCase();
    return ['single_end', 'all_tapes_and_fillers'].includes(category);
  }

  get showConstant2(): boolean {
    const category = this.form.get('category')?.value?.toLowerCase();
    return category === 'all_tapes_and_fillers';
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
